import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatNumber, numberWithCommas } from "../../utils";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";
import { SendSummary } from "./ConfirmSendModal";

const compact = "@media " + COMPACT_QUERY;

// Compact summary pill for the sticky footer — light lavender-gray rounded box,
// label on the left and bold value on the right, sized to its content (matches
// the send-page design reference). The footer container itself stays unchanged.
const FooterStat = ({ label, value }: { label: string; value: string }) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      gap: 2,
      flexShrink: 0,
      bgcolor: (theme) => theme.palette.primary.main + "14",
      borderRadius: 2,
      px: 1.75,
      py: 0.75,
      [compact]: { px: 1.25, py: 0.5, gap: 1.25 },
    }}
  >
    <Typography variant="body2" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} noWrap>
      {value}
    </Typography>
  </Stack>
);

interface SendSummaryFooterProps {
  /** Totals of the rows picked so far. */
  summary: SendSummary;
  /** What the count is a count of: จำนวนบิล on the bill list, จำนวนเอกสาร on the notes list. */
  countLabel: string;
  onConfirm: () => void;
}

/**
 * Sticky summary footer — fixed to the bottom of the content column, using the exact same
 * container style as the Sale Daily Report detail footer (Report/Details/Footer). Totals
 * reflect the currently selected rows.
 */
export default function SendSummaryFooter({
  summary,
  countLabel,
  onConfirm,
}: Readonly<SendSummaryFooterProps>) {
  return (
    <Paper
      sx={{
        // Offset by the live sidebar width so this fixed bar stays aligned
        // with the main content area instead of sliding under the sidebar.
        left: "var(--gb-sidebar-width, 0px)",
        right: 0,
        height: "66px",
        position: "fixed",
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "24px",
        boxShadow: "0 -4px 24px -8px rgba(0,0,0,0.10)",
        [compact]: {
          height: "auto",
          minHeight: "52px",
          padding: "8px 16px",
          gap: 1,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: 1.5,
          minWidth: 0,
          mr: 2,
          flexWrap: "wrap",
          [compact]: { gap: 1, mr: 1 },
        }}
      >
        <FooterStat label="มูลค่ารวมสุทธิ" value={formatNumber(summary.netTotal)} />
        <FooterStat label="มูลค่าก่อน VAT" value={formatNumber(summary.amountBeforeVat)} />
        <FooterStat label="ภาษีมูลค่าเพิ่ม (VAT)" value={formatNumber(summary.vatAmount)} />
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ flexShrink: 0, px: 0.5 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {countLabel}
          </Typography>
          <Typography variant="body2" fontWeight={700} noWrap>
            {numberWithCommas(summary.documentCount)}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={summary.documentCount === 0}
        >
          ยืนยัน
        </Button>
      </Stack>
    </Paper>
  );
}
